import React, { createContext, useContext, useState, useCallback } from "react";
import { TokenContext } from "./TokenContext";

const STORAGE_KEY_PREFIX = "applaby_cookie_consent";

const CookieConsentContext = createContext({
	thirdParty: false,
	decided: false,
	acceptAll: () => {},
	rejectAll: () => {},
	updateConsent: () => {},
	resetConsent: () => {},
});

function buildStorageKey(siteId) {
	return siteId ? `${STORAGE_KEY_PREFIX}_${siteId}` : STORAGE_KEY_PREFIX;
}

function getStoredConsent(key) {
	try {
		const raw = localStorage.getItem(key);
		if (!raw) return null;
		return JSON.parse(raw);
	} catch {
		return null;
	}
}

function storeConsent(key, consent) {
	try {
		localStorage.setItem(key, JSON.stringify(consent));
	} catch (e) {
		console.warn("Could not save cookie consent:", e);
	}
}

export function CookieConsentProvider({ children }) {
	const { appId } = useContext(TokenContext);
	const storageKey = buildStorageKey(appId);
	const stored = getStoredConsent(storageKey);

	const [consent, setConsent] = useState({
		thirdParty: stored?.thirdParty ?? false,
		decided: stored !== null,
	});

	const acceptAll = useCallback(() => {
		const next = { thirdParty: true, decided: true };
		setConsent(next);
		storeConsent(storageKey, { thirdParty: true });
	}, [storageKey]);

	const rejectAll = useCallback(() => {
		const next = { thirdParty: false, decided: true };
		setConsent(next);
		storeConsent(storageKey, { thirdParty: false });
	}, [storageKey]);

	const updateConsent = useCallback((patch) => {
		setConsent((prev) => {
			const next = { ...prev, ...patch, decided: true };
			storeConsent(storageKey, { thirdParty: next.thirdParty });
			return next;
		});
	}, [storageKey]);

	const resetConsent = useCallback(() => {
		try { localStorage.removeItem(storageKey); } catch {}
		setConsent({ thirdParty: false, decided: false });
	}, [storageKey]);

	return (
		<CookieConsentContext.Provider
			value={{ ...consent, acceptAll, rejectAll, updateConsent, resetConsent }}
		>
			{children}
		</CookieConsentContext.Provider>
	);
}

export function useCookieConsent() {
	return useContext(CookieConsentContext);
}

export default CookieConsentContext;
